import React, { useCallback, useContext, useEffect } from 'react';
import { SingleColumn } from 'components/FeaturedSpreadsheet/components';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { Cell } from 'components/Spreadsheet/components';
import { useLayoutContext } from 'context/LayoutContext';
import { equalizeSpreadsheetTitleContentRowHeights, syncSpreadsheetTitleContentRowHeights } from 'utilities';
import FastValueViewer from '../../utilities/FastValueViewer';
import { ALLOCATION_SCENARIOS_SLUG, VALUATION_GPC_TABLE_SLUG, VALUATION_SUMMARY_SLUG } from '../constants';
import { TitlesColumnProps } from './TitlesColumn.props';

const TitlesColumn = ({ className = '', data, dataEditor = undefined, tableSlug }: TitlesColumnProps) => {
  const { onCellsChanged } = useContext(FeaturedSpreadsheetContext);
  const { isExportMode } = useLayoutContext();

  const adjustSummaryValuationTitleCells = useCallback(() => {
    const valuationSummaryEmptyCells: NodeListOf<HTMLElement>
      = document.querySelectorAll('table tbody tr td.blank-cell');
    const valuationSummaryTitleCell: HTMLElement | null = document.querySelector(
      'table tbody tr td.spreadsheet-table-title-main > div'
    );

    if (valuationSummaryEmptyCells && valuationSummaryTitleCell) {
      let totalHeight = 0;
      valuationSummaryEmptyCells.forEach(emptyCell => {
        if (emptyCell.offsetHeight) {
          totalHeight += emptyCell.offsetHeight - 1.2;
        }
      });
      valuationSummaryTitleCell.style.height = `${totalHeight}px`;
    }
  }, []);

  const adjustAllocationTitleCells = useCallback(() => {
    const baseQuerySelector = 'table tbody tr td.spreadsheet-table-title-main > div';
    const allocationScenarioHeadersCells: NodeListOf<HTMLElement> = document.querySelectorAll(
      `#spreadsheet-table__content-columns ${baseQuerySelector}`
    );
    const allocationScenarioTitle: HTMLElement | null = document.querySelector(
      `#spreadsheet-table__title-columns ${baseQuerySelector}`
    );
    if (allocationScenarioHeadersCells && allocationScenarioTitle) {
      let largerHeight = 0;
      allocationScenarioHeadersCells.forEach(headerCell => {
        if (headerCell.offsetHeight > largerHeight) {
          largerHeight = headerCell.offsetHeight;
        }
      });
      allocationScenarioTitle.style.height = `${largerHeight}px`;
    }
  }, []);

  const adjustExportRowHeights = useCallback(() => {
    const container = document.getElementById(`spreadsheet-table__${tableSlug}`);
    if (!container) {
      return;
    }

    const titlesContainer = container.querySelector<HTMLElement>('.titles-columns-container');
    const contentContainer = container.querySelector<HTMLElement>('[id="spreadsheet-table__content-columns"]');
    if (!titlesContainer || !contentContainer) {
      return;
    }

    syncSpreadsheetTitleContentRowHeights(titlesContainer, contentContainer);
  }, [tableSlug]);

  const equalizeRowHeights = useCallback(() => {
    const container = document.getElementById(`spreadsheet-table__${tableSlug}`);
    if (!container) {
      return;
    }

    const titlesContainer = container.querySelector<HTMLElement>('.titles-columns-container');
    const contentContainer = container.querySelector<HTMLElement>('[id="spreadsheet-table__content-columns"]');
    if (!titlesContainer || !contentContainer) {
      return;
    }

    equalizeSpreadsheetTitleContentRowHeights(titlesContainer, contentContainer);
  }, [tableSlug]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (tableSlug === VALUATION_SUMMARY_SLUG) {
        adjustSummaryValuationTitleCells();
        if (isExportMode) {
          adjustExportRowHeights();
        }
      }
      if (tableSlug === ALLOCATION_SCENARIOS_SLUG) {
        adjustAllocationTitleCells();
      }
      if (tableSlug === VALUATION_GPC_TABLE_SLUG) {
        equalizeRowHeights();
      }
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [
    tableSlug,
    isExportMode,
    adjustSummaryValuationTitleCells,
    adjustAllocationTitleCells,
    adjustExportRowHeights,
    equalizeRowHeights,
    // Re-equalize after GPC row count changes (add/remove comparable companies)
    data,
  ]);

  return (
    <SingleColumn
      isLegend
      data={data}
      className={className}
      dataEditor={dataEditor}
      valueViewer={FastValueViewer}
      onCellsChanged={onCellsChanged}
      cellRenderer={Cell}
    />
  );
};

export default TitlesColumn;
